import { DragDropModule } from '@angular/cdk/drag-drop';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule, DatePipe } from '@angular/common';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { LetDirective } from '@ngrx/component';
import { TranslateModule } from '@ngx-translate/core';
import { HighchartsChartModule } from 'highcharts-angular';

import { ClientModule } from '@vi-iot/client';
import { ViessmannUIModule } from '@vi-iot/ui';

import {
  AggregatedStatusPillComponent,
  BannerComponent,
  CallbackNotificationComponent,
  DeviceFeaturesContainerComponent,
  DocumentsUpdateComponent,
  DowntimeNotificationComponent,
  FeatureListCustomItemComponent,
  FeaturesContainerComponent,
  FeatureTileComponent,
  FeatureTilesGridComponent,
  NoDataComponent,
  NotificationTileComponent,
  OrderContainerComponent,
  PillComponent,
  SidenavComponent,
  UpgradePackageBoxComponent,
} from '@components';
import {
  CancelBookingDirective,
  ForCountriesDirective,
  ForNotCountriesDirective,
  GtmDirective,
  HideForRsaUserDirective,
  HideIfGatewayTypeDirective,
  ShowForRsaRolesDirective,
  ShowForRsaUserDirective,
  TogglePanelDirective,
} from '@directives';
import {
  AcronymPipe,
  AddCharsPipe,
  CountryPipe,
  DashPipe,
  FormatPhoneNumberPipe,
  GenderPipe,
  LanguagePipe,
  SerialSpacingPipe,
  TruncatePipe,
  TrustHtmlPipe,
} from '@pipes';
import {
  AuthGuardService,
  CodeScannerService,
  DataService,
  DemoModeService,
  EnvironmentService,
  ForbiddenForRsaGuardService,
  InterceptorService,
  MaintainerAuthGuardService,
  NotAuthGuardService,
  ProPlusFeaturesGuardService,
  RoutingService,
  RsaAuthGuardService,
  SubscriptionsAuthGuardService,
  UIService,
} from '@services';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { NgxMaskModule } from 'ngx-mask';

import { InstallationSearchComponent } from '../+installations/installations/installation-search/installation-search.component';
import { environment } from '../../environments/environment';
import { OperationalDataCommonModule } from '../operational-data-common/operational-data-common.module';
import { BookingPackageBadgeComponent } from './components/booking-package-badge/booking-package-badge.component';

import { ToIntegerPipe } from '../operational-data-common/feature-pipes/to-integer.pipe';
import { CircleProgressBarComponent } from './circle-progress-bar/circle-progress-bar.component';
import { CancelBookingModalComponent } from './components/cancel-booking-modal/cancel-booking-modal.component';
import { ChipFilterComponent } from './components/chip-filters/chip-filter/chip-filter.component';
import { ChipFiltersComponent } from './components/chip-filters/chip-filters.component';
import { CommercialToolbarComponent } from './components/commercial-toolbar/commercial-toolbar.component';
import { ConnectivityRankingComponent } from './components/connectivity-ranking/connectivity-ranking.component';
import { DeviceTimeWarningComponent } from './components/device-time-warning/device-time-warning.component';
import { DropdownSelectComponent } from './components/dropdown-select/dropdown-select.component';
import { EditDeviceTimeComponent } from './components/edit-device-time/edit-device-time.component';
import { EditFeatureValueModalComponent } from './components/edit-feature-value-modal/edit-feature-value-modal.component';
import { EditSerialNumberModalComponent } from './components/edit-serial-number-modal/edit-serial-number-modal.component';
import { EventBannerComponent } from './components/event-banner/event-banner.component';
import { FeatureDetailsGridComponent } from './components/feature-details-grid/feature-details-grid.component';
import { FeatureListContainerComponent } from './components/feature-list-container/feature-list-container.component';
import { FeatureListItemComponent } from './components/feature-list-item/feature-list-item.component';
import { FeaturesContainerHeaderComponent } from './components/features-container-header/features-container-header.component';
import { InfiniteScrollComponent } from './components/infinite-scroll/infinite-scroll.component';
import { InformationPopupComponent } from './components/information-popup/information-popup.component';
import { InvitationListItemComponent } from './components/invitation-list-item/invitation-list-item.component';
import { InvitationNotificationComponent } from './components/invitation-notification/invitation-notification.component';
import { LearningSpaceBannerComponent } from './components/learning-space-banner/learning-space-banner.component';
import { MonthDatepickerComponent } from './components/month-datepicker/month-datepicker.component';
import { OptimizationRecommendationComponent } from './components/optimization-recommendation/optimization-recommendation.component';
import { PaidFeaturesBannerComponent } from './components/paid-features-banner/paid-features-banner.component';
import { PdfPreviewComponent } from './components/pdf-preview/pdf-preview.component';
import { PlantOverviewLinkComponent } from './components/plant-overview-link/plant-overview-link.component';
import { ProgramPanelComponent } from './components/program-panel/program-panel.component';
import { ErrorSearchResultsComponent } from './components/search-result-list/error-search-results/error-search-results.component';
import { GatewaysSearchResultsComponent } from './components/search-result-list/gateways-search-results/gateways-search-results.component';
import { InstallationsSearchResultsComponent } from './components/search-result-list/installations-search-results/installations-search-results.component';
import { InvitationsSearchResultsComponent } from './components/search-result-list/invitations-search-results/invitations-search-results.component';
import { SearchResultActiveErrorsPopupComponent } from './components/search-result-list/search-result-active-errors/search-result-active-errors-popup/search-result-active-errors-popup.component';
import { SearchResultActiveErrorsComponent } from './components/search-result-list/search-result-active-errors/search-result-active-errors.component';
import { SearchResultListComponent } from './components/search-result-list/search-result-list.component';
import { UsersSearchResultsComponent } from './components/search-result-list/users-search-results/users-search-results.component';
import { StatusPageComponent } from './components/status-page/status-page.component';
import { TranslateListComponent } from './components/translate-list/translate-list.component';
import { UpgradeCommercialBannerComponent } from './components/upgrade-commercial-modal/upgrade-commercial-modal.component';
import { ForCommercialUserDirective } from './directives/commercial-user/for-commercial-user.directive';
import { HideForCompanyUserDirective } from './directives/company-user/hide-for-company-user.directive';
import { HideForUserRoleDirective } from './directives/hide-for-user-role/hide-for-user-role.directive';
import { MatBounceButtonDirective } from './directives/mat-bounce-button/mat-bounce-button.directive';
import { HideForNoDashboardFeatureDirective } from './directives/no-dashboard-feature/hide-for-no-dashboard-feature.directive';
import { NotDraggableContentDirective } from './directives/not-draggable-content/not-draggable-content.directive';
import { PageScrollFetchDirective } from './directives/page-scroll-fetch/page-scroll-fetch.directive';
import { PreventEventDirective } from './directives/prevent-event/prevent-event.directive';
import { AbsoluteValuePipe } from './pipes/absolute-value/absolute-value.pipe';
import { BooleanSuffixPipe } from './pipes/boolean-suffix/boolean-suffix.pipe';
import { DateDiffMonthsPipe } from './pipes/date-diff-months/date-diff-months.pipe';
import { DateForSpecificTimezonePipe } from './pipes/date-for-specific-timezone/date-for-specific-timezone.pipe';
import { DecimalPipeWrapper } from './pipes/decimal-pipe-wrapper/decimal-pipe-wrapper.pipe';
import { GetUserRolePipe } from './pipes/getUserRole/get-user-role.pipe';
import { TranslateRolePipe } from './pipes/getUserRole/translate-role.pipe';
import { HoursToDaysPipe } from './pipes/hours-to-days/hours-to-days.pipe';
import { InputValuePipe } from './pipes/input-value/input-value.pipe';
import { LinkPipe } from './pipes/link/link.pipe';
import { MinutesOrHoursPipe } from './pipes/minutes-or-hours/minutes-or-hours.pipe';
import { MsToSecondsPipe } from './pipes/ms-to-seconds/ms-to-seconds.pipe';
import { PaidFeatureAvailable } from './pipes/paid-feature-available/paid-feature-available.pipe';
import { PropertyWithinPipe } from './pipes/propertyWithin/propertyWithin.pipe';
import { RegionPipe } from './pipes/region/region.pipe';
import { SecondsTimeFormatter } from './pipes/seconds-time-formatter/seconds-time-formatter.pipe';
import { SuffixPipe } from './pipes/suffix/suffix.pipe';
import { TooltipListPipe } from './pipes/tooltip-list/tooltip-list.pipe';
import { TranslateIncomingCountPipe } from './pipes/translate-incoming-count.pipe';
import { InstallationDetailsViewGuardService } from './services/installation-details-view-guard.service';
import { InvitationsAuthGuardService } from './services/invitations-auth-guard.service';
import { PartnerAccessGuardService } from './services/partner-access-guard.service';
import { RemoteDiagnosticsBackupGuardService } from './services/remote-diagnostics-backup-guard.service';
import { RemoteDiagnosticsGuardService } from './services/remote-diagnostics-guard.service';
import { OptimizationSettingOptionComponent } from '../+installations/installation/hems/hems-settings/hems-financial-optimization/optimization-setting-option/optimization-setting-option.component';
import { TextParserService } from './services/text-parser-service.service';

export const STANDALONE = [
  ForCommercialUserDirective,
  GtmDirective,
  HideForRsaUserDirective,
  PageScrollFetchDirective,
  SidenavComponent,
  DowntimeNotificationComponent,
  CallbackNotificationComponent,
  TrustHtmlPipe,
  FormatPhoneNumberPipe,
  DashPipe,
  CountryPipe,
  RegionPipe,
  GenderPipe,
  LanguagePipe,
  BooleanSuffixPipe,
  AbsoluteValuePipe,
  TooltipListPipe,
  ToIntegerPipe,
  AggregatedStatusPillComponent,
  PillComponent,
  BookingPackageBadgeComponent,
  InfiniteScrollComponent,
  BannerComponent,
  ChipFiltersComponent,
  ChipFilterComponent,
  CancelBookingDirective,
  NotDraggableContentDirective,
  ConnectivityRankingComponent,
  PdfPreviewComponent,
  ConnectivityRankingComponent,
  StatusPageComponent,
  OptimizationSettingOptionComponent,
];

@NgModule({
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule.withConfig({
      callSetDisabledState: 'whenDisabledForLegacyCode',
    }),
    TranslateModule,
    ScrollingModule,
    ClientModule.forRoot(environment.clientConfig),
    ViessmannUIModule, // Includes FlexLayout Module & Material Modules
    DragDropModule,
    OperationalDataCommonModule,
    HighchartsChartModule,
    PdfViewerModule,
    LetDirective,
    NgxMaskModule,
    ...STANDALONE,
  ],
  exports: [
    PdfViewerModule,
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    ScrollingModule,
    ClientModule,
    ViessmannUIModule,
    DragDropModule,
    OperationalDataCommonModule,
    OrderContainerComponent,
    OptimizationRecommendationComponent,
    LetDirective,
    NgxMaskModule,

    // Pipes
    AcronymPipe,
    AddCharsPipe,

    GetUserRolePipe,

    LinkPipe,
    PropertyWithinPipe,
    MinutesOrHoursPipe,
    MsToSecondsPipe,
    TranslateIncomingCountPipe,
    TranslateRolePipe,
    HoursToDaysPipe,
    TruncatePipe,
    SuffixPipe,
    PaidFeatureAvailable,
    DecimalPipeWrapper,
    DateForSpecificTimezonePipe,
    SecondsTimeFormatter,
    SerialSpacingPipe,
    DateDiffMonthsPipe,
    TooltipListPipe,
    InputValuePipe,

    // Components
    SidenavComponent,
    NoDataComponent,
    FeatureTilesGridComponent,
    FeaturesContainerComponent,
    DeviceFeaturesContainerComponent,
    FeatureTileComponent,
    ProgramPanelComponent,
    SearchResultListComponent,
    InvitationNotificationComponent,
    NotificationTileComponent,
    InstallationSearchComponent,
    StatusPageComponent,
    DocumentsUpdateComponent,
    LearningSpaceBannerComponent,
    InvitationListItemComponent,
    PlantOverviewLinkComponent,
    EventBannerComponent,
    CircleProgressBarComponent,
    DropdownSelectComponent,
    PaidFeaturesBannerComponent,
    BookingPackageBadgeComponent,
    MonthDatepickerComponent,
    CancelBookingModalComponent,
    InformationPopupComponent,
    FeatureListCustomItemComponent,
    UpgradePackageBoxComponent,
    CommercialToolbarComponent,
    UpgradeCommercialBannerComponent,
    ConnectivityRankingComponent,
    EditDeviceTimeComponent,
    DeviceTimeWarningComponent,

    // Directives
    ForCountriesDirective,
    ForNotCountriesDirective,
    GtmDirective,
    HideForCompanyUserDirective,
    HideIfGatewayTypeDirective,
    ShowForRsaRolesDirective,
    ShowForRsaUserDirective,
    TogglePanelDirective,
    ForCommercialUserDirective,
    HideForUserRoleDirective,

    PlantOverviewLinkComponent,
    FeatureListContainerComponent,
    FeatureListItemComponent,
    FeatureDetailsGridComponent,
    PreventEventDirective,
    MatBounceButtonDirective,
    NotDraggableContentDirective,
    HideForNoDashboardFeatureDirective,
    TranslateListComponent,
    ...STANDALONE,
  ],
  declarations: [
    NoDataComponent,
    FeatureTilesGridComponent,
    FeaturesContainerComponent,
    DeviceFeaturesContainerComponent,
    FeatureTileComponent,
    ProgramPanelComponent,
    SearchResultListComponent,
    InvitationNotificationComponent,
    NotificationTileComponent,
    InstallationSearchComponent,
    LearningSpaceBannerComponent,
    InvitationListItemComponent,
    MonthDatepickerComponent,
    OrderContainerComponent,
    FeatureListCustomItemComponent,
    OptimizationRecommendationComponent,
    UpgradePackageBoxComponent,
    CommercialToolbarComponent,
    UpgradeCommercialBannerComponent,

    // Pipes
    AcronymPipe,
    AddCharsPipe,
    GetUserRolePipe,
    LinkPipe,
    PropertyWithinPipe,
    MinutesOrHoursPipe,
    MsToSecondsPipe,
    TranslateIncomingCountPipe,
    TranslateRolePipe,
    HoursToDaysPipe,
    TruncatePipe,
    DecimalPipeWrapper,
    DateForSpecificTimezonePipe,
    SecondsTimeFormatter,

    // Directives
    ForCountriesDirective,
    ForNotCountriesDirective,
    HideForCompanyUserDirective,
    HideIfGatewayTypeDirective,
    ShowForRsaRolesDirective,
    ShowForRsaUserDirective,
    TogglePanelDirective,
    PreventEventDirective,
    MatBounceButtonDirective,
    HideForNoDashboardFeatureDirective,
    HideForUserRoleDirective,

    // Service app
    DocumentsUpdateComponent,
    InvitationNotificationComponent,
    UsersSearchResultsComponent,
    ErrorSearchResultsComponent,
    SearchResultActiveErrorsComponent,
    SearchResultActiveErrorsPopupComponent,
    GatewaysSearchResultsComponent,
    InstallationsSearchResultsComponent,
    InvitationsSearchResultsComponent,
    EditSerialNumberModalComponent,
    SuffixPipe,
    PaidFeatureAvailable,
    PlantOverviewLinkComponent,
    FeatureListContainerComponent,
    FeatureListItemComponent,
    FeatureDetailsGridComponent,
    EventBannerComponent,
    CircleProgressBarComponent,
    DropdownSelectComponent,
    PaidFeaturesBannerComponent,
    PreventEventDirective,
    EditFeatureValueModalComponent,
    CancelBookingModalComponent,
    InformationPopupComponent,
    FeatureListCustomItemComponent,
    FeaturesContainerHeaderComponent,
    DateDiffMonthsPipe,
    InputValuePipe,
    SerialSpacingPipe,
    TranslateListComponent,
    EditDeviceTimeComponent,
    DeviceTimeWarningComponent,
  ],
  providers: [
    DatePipe,
    DecimalPipeWrapper,
    SerialSpacingPipe,
    CodeScannerService,
    DataService,
    AuthGuardService,
    MaintainerAuthGuardService,
    NotAuthGuardService,
    ForbiddenForRsaGuardService,
    InvitationsAuthGuardService,
    RemoteDiagnosticsGuardService,
    RemoteDiagnosticsBackupGuardService,
    RsaAuthGuardService,
    SubscriptionsAuthGuardService,
    ProPlusFeaturesGuardService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: InterceptorService,
      multi: true,
    },
    UIService,
    RoutingService,
    DemoModeService,
    EnvironmentService,
    PartnerAccessGuardService,
    InstallationDetailsViewGuardService,
    TextParserService,
  ],
})
export class SharedDependenciesModule {}
